import { motion } from "framer-motion";
import { Briefcase } from "lucide-react";
import React, { useState } from "react";
import { NewExperienceItemType } from "../experience/experience-timeline";
import Link from "next/link";

// Timeline Component
export const Timeline = ({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-bold text-center mb-16 text-slate-800"
        >
          {title}
        </motion.h1>
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-400 via-purple-400 to-blue-400" />

          {/* Timeline Dots */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full flex flex-col justify-between py-8">
            {React.Children.map(children, (_, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.2, type: "spring" }}
                className="w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow-lg"
              />
            ))}
          </div>

          <div className="space-y-12 md:space-y-24">{children}</div>
        </div>
      </div>
    </div>
  );
};

// Timeline Item Component
export const TimelineItem = ({
  position = "left",
  icon: Icon = Briefcase,
  title,
  description,
  date,
  bullets,
  skills = [],
  delay = 0,
}: NewExperienceItemType & { delay?: number }) => {
  const [isHovered, setIsHovered] = useState(false);

  const isLeft = position === "left";

  return (
    <motion.div
      initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.5, delay }}
      className={`relative flex ${isLeft ? "justify-start" : "justify-end"} mb-8`}
    >
      <motion.div
        whileHover={{ scale: 1.02, y: -5 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className={`w-full md:w-5/12 ${isLeft ? "md:pr-12" : "md:pl-12"}`}
      >
        <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-slate-200">
          {/* Icon Header */}
          <div
            className={`p-6 ${isLeft ? "bg-gradient-to-r from-blue-500 to-blue-600" : "bg-gradient-to-r from-purple-500 to-purple-600"}`}
          >
            <motion.div
              animate={{ rotate: isHovered ? 360 : 0 }}
              transition={{ duration: 0.6 }}
              className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-3"
            >
              <Icon
                className={`w-6 h-6 ${isLeft ? "text-blue-600" : "text-purple-600"}`}
              />
            </motion.div>
            <h3 className="text-xl font-bold text-white mb-1">{title}</h3>
            {date && (
              <p className="text-blue-100 text-sm font-medium">{date}</p>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {/* <p className="text-slate-700 leading-relaxed mb-4">{description}</p> */}

            {bullets ? (
              <ul className="list-disc space-y-2">
                {bullets.slice(0, 3).map((bullet, index) => (
                  <li
                    key={index}
                    className="text-sm text-muted-foreground hover:text-black dark:hover:text-white"
                  >
                    {bullet}
                  </li>
                ))}
                {bullets.length > 3 && (
                  <li className="text-sm text-muted-foreground">
                    + {bullets.length - 3} more bullets,{" "}
                    <Link
                      href="/resume"
                      className="text-primary hover:underline hover:text-blue-700 dark:hover:text-blue-300"
                    >
                      See here
                    </Link>
                  </li>
                )}
              </ul>
            ) : (
              <p className="text-muted-foreground text-sm leading-relaxed hover:text-black dark:hover:text-white">
                {description}
              </p>
            )}

            {skills.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {skills.map((tag, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 * index }}
                    className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      isLeft
                        ? "bg-blue-50 text-blue-700 border border-blue-200"
                        : "bg-purple-50 text-purple-700 border border-purple-200"
                    }`}
                  >
                    {tag}
                  </motion.span>
                ))}
              </div>
            )}
          </div>

          {/* Decorative Corner */}
          <motion.div
            animate={{ scale: isHovered ? 1.2 : 1 }}
            className={`absolute top-0 ${isLeft ? "right-0" : "left-0"} w-20 h-20 ${
              isLeft ? "bg-blue-500" : "bg-purple-500"
            } opacity-5 rounded-full -m-10`}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};
